import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import {
  Bell, CheckCircle2, XCircle, AlertTriangle, Eye, RotateCcw,
  Check, CheckCheck, MessageSquare, FileText, ClipboardCheck, Trophy
} from 'lucide-react';

const typeIcons = {
  job_completed: CheckCircle2,
  job_completed_with_errors: AlertTriangle,
  job_failed: XCircle,
  share_accessed: Eye,
  retry_completed: RotateCcw,
  division_audit: CheckCircle2,
  rfi_high_value_alert: AlertTriangle,
  new_message: MessageSquare,
  rfi_response: FileText,
  submittal_update: ClipboardCheck,
  milestone_triggered: Trophy,
};

const typeColors = {
  job_completed: 'text-emerald-400',
  job_completed_with_errors: 'text-amber-400',
  job_failed: 'text-rose-400',
  share_accessed: 'text-blue-400',
  retry_completed: 'text-violet-400',
  division_audit: 'text-cyan-400',
  rfi_high_value_alert: 'text-primary',
  new_message: 'text-blue-400',
  rfi_response: 'text-amber-400',
  submittal_update: 'text-emerald-400',
  milestone_triggered: 'text-amber-400',
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  const fetchUnreadCount = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.unread_count);
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch {}
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    setOpen(false);
    if (notif.link) {
      navigate(notif.link);
    } else if (notif.job_id) {
      navigate(`/jobs/${notif.job_id}`);
    }
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className="relative p-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
      >
        <Bell className="w-4.5 h-4.5" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 flex items-center justify-center rounded-full bg-amber-500 text-[9px] font-mono font-bold text-white"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-96 bg-card border border-border rounded-sm shadow-2xl z-50 animate-fade-in"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-heading font-bold text-xs uppercase tracking-wider text-foreground">
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                data-testid="mark-all-read-btn"
                className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground hover:text-primary transition-colors"
              >
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-auto divide-y divide-slate-800/30">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                <p className="text-xs text-muted-foreground font-mono">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = typeIcons[n.type] || Bell;
                const color = typeColors[n.type] || 'text-muted-foreground';
                return (
                  <div
                    key={n.id}
                    onClick={() => handleClick(n)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleClick(n); }}
                    data-testid={`notification-item-${n.id}`}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-secondary/30 transition-colors cursor-pointer ${
                      !n.read ? 'bg-secondary/10' : ''
                    }`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-heading text-xs font-bold tracking-wide ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {n.title}
                        </span>
                        {!n.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground font-mono leading-relaxed mt-0.5 line-clamp-2">
                        {n.message}
                      </p>
                      <span className="text-[9px] text-muted-foreground font-mono mt-1 block">
                        {timeAgo(n.created_at)}
                      </span>
                    </div>
                    {!n.read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                        className="p-1 shrink-0 text-muted-foreground hover:text-emerald-400 transition-colors"
                        title="Mark as read"
                      >
                        <Check className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
